import DevErrorToast from "@/components/DevErrorToast"
import Toast from "@/components/Toast"
import { useAddCSFlagMutation, useRemoveCSFlagMutation } from "@/data/queries"
import { CustomerSupportPatient } from "@/model/CustomerSupport"
import { useState } from "react"
import { toast } from "sonner"
import CSFlagButton from "./CSFlagButton"
import CSFlagDialog from "./CSFlagDialog"

type CSFlagCellProps = {
  patient: CustomerSupportPatient
}

const CSFlagCell = ({ patient }: CSFlagCellProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { mutateAsync: addCSFlag, isPending: isAddingFlag } =
    useAddCSFlagMutation(patient.bloomlifeId)
  const { mutateAsync: removeCSFlag, isPending: isRemovingFlag } =
    useRemoveCSFlagMutation(patient.bloomlifeId)

  const isPending = isAddingFlag || isRemovingFlag

  const handleOpenDialog = () => {
    setIsDialogOpen(true)
  }

  const handleSave = async (reason: string) => {
    try {
      await addCSFlag({ bloomlifeId: patient.bloomlifeId, reason })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="CS flag saved successfully." />
      ))
      setIsDialogOpen(false)
    } catch (error) {
      toast.custom(
        (toastId) => (
          <DevErrorToast
            toastId={toastId}
            userMessage="Something went wrong while saving the CS flag. Try again, or contact the dev team."
            error={error}
          />
        ),
        { duration: Infinity }
      )
    }
  }

  const handleRemove = async () => {
    try {
      await removeCSFlag({ bloomlifeId: patient.bloomlifeId })
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="CS flag removed successfully." />
      ))
      setIsDialogOpen(false)
    } catch (error) {
      toast.custom(
        (toastId) => (
          <DevErrorToast
            toastId={toastId}
            userMessage="Something went wrong while removing the CS flag. Try again, or contact the dev team."
            error={error}
          />
        ),
        { duration: Infinity }
      )
    }
  }

  return (
    <>
      <div className="flex justify-start">
        <CSFlagButton
          isFlagged={!!patient.customerSupportNotes && patient.customerSupportNotes.flagged}
          csFlagNotes={patient.customerSupportNotes?.reason}
          onClick={handleOpenDialog}
          isPending={isPending}
        />
      </div>
      <CSFlagDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        csFlag={!!patient.customerSupportNotes && patient.customerSupportNotes.flagged}
        currentReason={patient.customerSupportNotes?.reason}
        onSave={handleSave}
        onRemove={handleRemove}
        isSaving={isAddingFlag}
        isRemoving={isRemovingFlag}
      />
    </>
  )
}

export default CSFlagCell
